'use client'

import { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo, ReactNode } from 'react'
import { useUser } from '@clerk/nextjs'
import { getImpersonationSession } from '@/lib/admin-impersonation'

interface UserProfile {
  id: string
  clerkUserId?: string
  profilePictureUrl?: string
  presignedProfilePictureUrl?: string
  fullName?: string
  companyName?: string
  role?: string
  name?: string
  email?: string
  phone?: string
  timezone?: string
}

interface UserProfileContextValue {
  profile: UserProfile | null
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
}

const UserProfileContext = createContext<UserProfileContextValue | undefined>(undefined)

export function UserProfileProvider({ children }: { children: ReactNode }) {
  const { user } = useUser()
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const consecutiveFailuresRef = useRef(0)
  const isBlockedRef = useRef(false)
  const blockedForUserIdRef = useRef<string | null>(null)
  const lastFetchTimeRef = useRef(0)
  const lastFetchUserIdRef = useRef<string | null>(null)
  const hasProfileRef = useRef(false)
  const FETCH_COOLDOWN = 10000 // 10s minimum between automatic fetches
  const FORCE_FETCH_MIN_INTERVAL = 800 // Hard minimum between ANY fetches, even forced ones

  const fetchProfile = useCallback(async (options?: { force?: boolean }) => {
    if (!user?.id) {
      setProfile(null)
      setIsLoading(false)
      return
    }

    const now = Date.now()

    // Hard minimum interval — prevents rapid-fire loops from any event source
    if (now - lastFetchTimeRef.current < FORCE_FETCH_MIN_INTERVAL) {
      return
    }

    // Client-side cooldown: skip if fetched recently for the same user (unless forced by user action)
    if (!options?.force && lastFetchUserIdRef.current === user.id && now - lastFetchTimeRef.current < FETCH_COOLDOWN) {
      return
    }

    // Circuit breaker: Stop retrying after 3 consecutive auth failures.
    // Only reset if a different user has signed in (not on JWT refresh flicker).
    if (isBlockedRef.current && blockedForUserIdRef.current === user.id) {
      console.log('🚫 Profile fetch blocked due to repeated auth failures')
      setIsLoading(false)
      return
    }

    // Different user — reset circuit breaker and cooldown
    if (blockedForUserIdRef.current !== user.id) {
      consecutiveFailuresRef.current = 0
      isBlockedRef.current = false
      blockedForUserIdRef.current = null
    }

    try {
      // Only show loading spinner on the initial load (no profile yet).
      // Background refreshes update silently to avoid re-triggering loading states.
      // Use a ref — not the `profile` state — to avoid stale closure issues inside useCallback.
      if (!hasProfileRef.current) {
        setIsLoading(true)
      }

      // Check for impersonation context and add headers if needed
      const headers: HeadersInit = {}

      if (typeof window !== 'undefined') {
        // Use getImpersonationSession without parameter to find any active session
        // This is safer because it will search for any admin-specific session
        const impersonationSession = getImpersonationSession()

        if (impersonationSession) {
          console.log('🎭 FRONTEND: Adding impersonation headers to profile request', {
            impersonatedUserId: impersonationSession.impersonatedUser.id,
            adminId: impersonationSession.adminId,
            currentClerkUserId: user?.id
          })
          headers['x-impersonated-user-id'] = impersonationSession.impersonatedUser.id
          headers['x-admin-user-id'] = impersonationSession.adminId
        }
      }

      const response = await fetch('/api/user/profile', { headers })

      if (response.ok) {
        const data = await response.json()
        let profileData = data.profile

        // If user has a profile picture, fetch presigned URL
        if (profileData?.profilePictureUrl) {
          try {
            const presignedResponse = await fetch('/api/user/profile-picture', { headers })
            if (presignedResponse.ok) {
              const presignedData = await presignedResponse.json()
              profileData = {
                ...profileData,
                presignedProfilePictureUrl: presignedData.profilePictureUrl
              }
            }
          } catch (err) {
            console.error('Error fetching presigned profile picture URL:', err)
            // Continue without presigned URL
          }
        }

        setProfile(profileData)
        setError(null)
        consecutiveFailuresRef.current = 0 // Reset on success
        lastFetchTimeRef.current = Date.now() // Record successful fetch time for cooldown
        lastFetchUserIdRef.current = user?.id ?? null // Track which user was fetched
        hasProfileRef.current = true // Mark profile as loaded for future background refreshes
      } else {
        // Handle authentication failures
        if (response.status === 401) {
          consecutiveFailuresRef.current++
          console.log(`⚠️ Auth failure ${consecutiveFailuresRef.current}/3`)

          // After 3 consecutive failures, block further requests
          if (consecutiveFailuresRef.current >= 3) {
            isBlockedRef.current = true
            blockedForUserIdRef.current = user?.id ?? null
            console.log('🚫 Blocking profile fetches - session appears invalid')
          }
        } else {
          setError('Failed to load profile')
          consecutiveFailuresRef.current = 0
        }
        setProfile(null)
      }
    } catch (err) {
      console.error('Error fetching user profile:', err)
      setError('Failed to load profile')
      setProfile(null)
      consecutiveFailuresRef.current = 0
    } finally {
      setIsLoading(false)
    }
  }, [user?.id])

  useEffect(() => {
    // Only fetch when we have a valid user ID
    if (user?.id) {
      fetchProfile()
    } else {
      setProfile(null)
      setIsLoading(false)
      hasProfileRef.current = false
    }
  }, [user?.id, fetchProfile])

  // Listen for profile update events
  useEffect(() => {
    const handleProfileUpdate = () => {
      if (user?.id) {
        fetchProfile({ force: true })
      }
    }

    // Listen for impersonation changes to refetch profile
    const handleImpersonationChange = () => {
      if (user?.id) {
        // Reset cooldown refs so the forced fetch isn't throttled
        lastFetchTimeRef.current = 0
        lastFetchUserIdRef.current = null
        // Reset blocked state to allow fresh fetch
        isBlockedRef.current = false
        consecutiveFailuresRef.current = 0
        blockedForUserIdRef.current = null
        // Keep hasProfileRef.current = true so fetchProfile does NOT set isLoading=true.
        // We're keeping the old profile visible until the new one arrives — no spinner needed.
        // The profile will silently swap once the fetch completes.
        fetchProfile({ force: true })
      }
    }

    window.addEventListener('userProfileUpdated', handleProfileUpdate)
    window.addEventListener('impersonationChanged', handleImpersonationChange)
    
    return () => {
      window.removeEventListener('userProfileUpdated', handleProfileUpdate)
      window.removeEventListener('impersonationChanged', handleImpersonationChange)
    }
  }, [user?.id, fetchProfile])

  const value = useMemo(() => ({
    profile,
    isLoading,
    error,
    refetch: () => fetchProfile({ force: true })
  }), [profile, isLoading, error, fetchProfile])

  return (
    <UserProfileContext.Provider value={value}>
      {children}
    </UserProfileContext.Provider>
  )
}

export function useUserProfile() {
  const context = useContext(UserProfileContext)
  if (context === undefined) {
    throw new Error('useUserProfile must be used within a UserProfileProvider')
  }
  return context
}
